import type { FC } from 'react';
import type { IEvaultApi } from './use-evault-api';
import type { IVaultDocument } from './types';

import { useCallback, useEffect, useRef } from 'react';

import { useFaceVerification, useNotification } from 'hooks';
import { KV, Modal, StatusChip } from 'views/design-system';

import { FaceVerify } from './face-verify';
import type { IFaceCaptureOutcome } from './face-verify';
import { formatDisplayId, money } from './derive';
import { useTransfer } from './use-transfer';

/** `FACE_NOT_FOUND` / `FACE_SERVICE_UNAVAILABLE` → the copy the panel shows; any other code falls through to the server's own message. */
const FACE_ERROR_COPY: Partial<Record<string, string>> = {
	FACE_NOT_FOUND:
		'We could not see a face. Face the camera in good light and try again.',
	FACE_SERVICE_UNAVAILABLE:
		'Face verification is unavailable right now. Try again in a few minutes.',
};

export interface ITransferModalProps {
	api: IEvaultApi;
	/** The documents being transferred, resolved by the caller. */
	documents: IVaultDocument[];
	onClose: () => void;
	/** Committed and acknowledged: clear the selection and refetch. */
	onTransferred: () => void;
	/** The list is stale (409) but the modal is still up. */
	onRefresh: () => void;
	/** 403 `EVAULT_NOT_ENTITLED` — close and open the add-on instead. */
	onUnentitled: () => void;
	/** The server-owned per-document price (`GET /evault-addons` → `status.transferPrice`). */
	transferPrice: number;
}

/**
 * Transfer ownership (§8d) — four steps in ONE modal.
 *
 * Mounted only while open, so its state starts fresh on every attempt rather
 * than needing a reset-on-reopen effect (and so a half-finished selection can
 * never be inherited by the next one).
 *
 * WHAT IS AND IS NOT CLAIMED ON THE WAY OUT. The `done` step prints only what
 * the commit response actually asserted: the notification line appears only
 * when the server says the new-owner email was sent or is on its way
 * (`notificationSent` / `notificationQueued`) — and there is no encryption line at all. `encryptionVerified` is a hard
 * `false` on the server precisely because nothing in this stack reads the
 * storage layer back, and the Figma's "AES-256 Encryption" tile would be the UI
 * asserting a control nobody has measured (C2).
 *
 * NO CARD CHARGED AT TRANSFER TIME (D10). `confirm` shows the fee the commit
 * will meter — the server's own count × the server's own
 * `transferPrice` — but nothing here collects payment; it is added to this
 * month's invoice, the same way the eVault add-on itself is (spec
 * 2026-09-09). The Figma's "Confirm payment" frame has nothing behind it here.
 */
export const TransferModal: FC<ITransferModalProps> = ({
	api,
	documents,
	onClose,
	onTransferred,
	onRefresh,
	onUnentitled,
	transferPrice,
}) => {
	const { successNotification } = useNotification();
	const {
		targets,
		noTargetsReason,
		step,
		email,
		setEmail,
		entityName,
		setEntityName,
		entityKind,
		setIndividual,
		canInitiate,
		keepRetentionCopy,
		setKeepRetentionCopy,
		busy,
		error,
		conflicted,
		initiated,
		committed,
		fee,
		initiate,
		verified,
		back,
		commit,
		cancelPending,
	} = useTransfer({ api, documents, onUnentitled, onRefresh, transferPrice });
	const { verify: verifyFace, busy: faceBusy } = useFaceVerification();

	/* The one caller of `useFaceVerification` — it owns the pending transfer's
	   id, so it is the one that can turn a capture into a call. The outcome
	   goes BACK to the panel rather than into the shared `error` slot: the
	   panel has a review screen now, and a refusal belongs under the photo
	   that was refused, with Retake beside it. Success is handed to
	   `useTransfer.verified` only when the user continues past the verified
	   screen (`onContinue`), so the step machine still sees one clean
	   transition. Codes map to R6's copy; anything else falls through to the
	   server's own message. */
	const onCaptured = useCallback(
		async (images: string[]): Promise<IFaceCaptureOutcome> => {
			if (!initiated) {
				return { ok: false, message: 'This transfer is no longer pending.' };
			}
			const result = await verifyFace(initiated._id, images);
			if (result.ok) return { ok: true, token: result.token };
			return {
				ok: false,
				message: FACE_ERROR_COPY[result.code] ?? result.message,
			};
		},
		[initiated, verifyFace]
	);

	/* One toast per commit, fired where the commit lands rather than where the
	   modal is dismissed — the transfer is done at that moment whether or not
	   anyone presses Done. */
	const toasted = useRef(false);
	useEffect(() => {
		if (!committed || toasted.current) return;
		toasted.current = true;
		const who =
			committed.newOwner?.name || committed.newOwner?.email || 'the new owner';
		successNotification(
			`${committed.transferredCount} original(s) transferred to ${who}`
		);
	}, [committed, successNotification]);

	/**
	 * ×, backdrop and Escape all land here.
	 *
	 * Leaving `verify` or `confirm` by any route cancels the pending record —
	 * the same call Back makes. Skipping it because the user pressed Escape
	 * rather than Back would leave a pending transfer to expire on its own,
	 * which is exactly the state R6 says not to leave behind. And closing on
	 * `done` is Done: the originals have already moved, so the list behind the
	 * modal is wrong until it refetches.
	 */
	const close = useCallback(() => {
		if (busy || faceBusy) return;
		if (step === 'done') {
			onTransferred();
			return;
		}
		if ((step === 'verify' || step === 'confirm') && !conflicted) {
			void cancelPending().then(onClose);
			return;
		}
		onClose();
	}, [busy, cancelPending, conflicted, faceBusy, onClose, onTransferred, step]);

	const newOwner = initiated?.newOwner;
	const summary = initiated?.summary;
	/* Named rather than written into the `KV` rows literal, which eslint reads
	   as a list of children wanting keys. */
	const newOwnerCell = (
		<span title={newOwner?.email}>{newOwner?.name || newOwner?.email || '—'}</span>
	);
	/* The SERVER's copy of the entity, not the field's — trimmed and defaulted by the
	   validator, so this row shows what was recorded rather than what was typed. The
	   chip is what distinguishes a person acting as their own entity from a company
	   that happens to be named after one; without it the two rows read identically. */
	const entityCell = newOwner?.entity ? (
		<span>
			{newOwner.entity.name}
			{newOwner.entity.kind === 'individual' ? (
				<>
					{' '}
					<StatusChip verdict="ns">INDIVIDUAL</StatusChip>
				</>
			) : null}
		</span>
	) : null;

	const title =
		step === 'choose'
			? 'Transfer ownership'
			: step === 'verify'
			? "Verify it's you"
			: step === 'confirm'
			? 'Confirm transfer'
			: 'Ownership transferred';

	return (
		<Modal
			open
			title={title}
			size="wide"
			className="evault-modal"
			sub={
				step === 'choose'
					? "Transfer of the original to one of the agreement's signers."
					: undefined
			}
			onClose={close}
			footer={
				step === 'choose' ? (
					<>
						<button className="dbtn" type="button" disabled={busy} onClick={close}>
							Cancel
						</button>
						<button
							className="dbtn pri"
							type="button"
							disabled={busy || !canInitiate}
							onClick={() => void initiate()}
						>
							Continue
						</button>
					</>
				) : step === 'verify' ? (
					<button
						className="dbtn"
						type="button"
						disabled={faceBusy}
						onClick={() => void back()}
					>
						Back
					</button>
				) : step === 'confirm' ? (
					conflicted ? (
						<button className="dbtn pri" type="button" onClick={onClose}>
							Close
						</button>
					) : (
						<>
							<button
								className="dbtn"
								type="button"
								disabled={busy}
								onClick={() => void back()}
							>
								Back
							</button>
							<button
								className="dbtn pri"
								type="button"
								disabled={busy || !fee.unit}
								onClick={() => void commit()}
							>
								Confirm and transfer
							</button>
						</>
					)
				) : (
					<button className="dbtn pri" type="button" onClick={onTransferred}>
						Done
					</button>
				)
			}
		>
			{step === 'choose' && (
				<>
					<div className="tcount">
						{documents.length} document{documents.length === 1 ? '' : 's'} selected
					</div>
					<div className="tchips">
						{documents.map(doc => (
							<span className="vch strt" key={doc._id} title={doc.name}>
								{formatDisplayId(doc.displayId)} {doc.name}
							</span>
						))}
					</div>

					<div className="tlab">Transfer to</div>
					<div className="thelp">
						An original can only be transferred to someone who signed the
						agreement. A signer with no account here receives it by email.
					</div>

					{targets.length ? (
						<div className="targets" role="radiogroup" aria-label="Transfer to">
							{targets.map(target => (
								<label className="target" key={target.email}>
									<input
										type="radio"
										name="evault-transfer-target"
										value={target.email}
										checked={email === target.email}
										disabled={busy}
										onChange={() => setEmail(target.email)}
									/>
									<span className="tmeta">
										<span className="nm">{target.name}</span>
										<span className="mail">{target.email}</span>
									</span>
								</label>
							))}
						</div>
					) : (
						<div className="sv2-empty">
							{noTargetsReason === 'only-you'
								? documents.length === 1
									? 'You are the only signer of this agreement, so there is no one to transfer it to. An original can only go to another person who signed it.'
									: 'You are the only person who signed all of the selected documents, so there is no one to transfer them to.'
								: noTargetsReason === 'no-common'
									? 'No one else signed all of the selected documents. Select fewer documents, or transfer them one at a time.'
									: 'No signers are recorded on this document, so it cannot be transferred.'}
						</div>
					)}

					{/* THE LEGAL OWNER, asked for beside the person.
					    The radio list above names a *signer*; this names the
					    organisation, corporation or entity that will own the original,
					    which is what compliance reads back. A natural person is their
					    own entity — the checkbox says so rather than leaving a reader
					    to guess why a person's name is in a field labelled
					    "organisation". */}
					<div className="tlab" id="evault-entity-label">
						Owning entity
					</div>
					<div className="thelp">
						The organisation, corporation or entity that will own the document.
					</div>
					<div className="fld ent">
						<input
							value={entityName}
							placeholder="e.g. Acme Holdings LLC"
							maxLength={120}
							disabled={busy}
							aria-labelledby="evault-entity-label"
							onChange={e => setEntityName(e.target.value)}
						/>
					</div>
					<label className="chk">
						<input
							type="checkbox"
							checked={entityKind === 'individual'}
							disabled={busy}
							onChange={e => setIndividual(e.target.checked)}
						/>
						<span>
							<span className="ctitle">
								The new owner is a person, not an organisation
							</span>
							<span className="fhint">Their own name is the entity.</span>
						</span>
					</label>

					{/* The server's own sentence, under the two fields that caused it —
					    the recipient and the entity are both refused from this one call. */}
					{!!error && <div className="merr">{error}</div>}

					<label className="chk">
						<input
							type="checkbox"
							checked={keepRetentionCopy}
							disabled={busy}
							onChange={e => setKeepRetentionCopy(e.target.checked)}
						/>
						<span>
							<span className="ctitle">Keep a watermarked copy for myself</span>
							<span className="fhint">
								You keep a copy clearly marked as not the original. The original
								itself moves to the new owner.
							</span>
						</span>
					</label>

					<div className="note warn">
						This moves the original permanently. After the transfer you will no
						longer own it.
					</div>
				</>
			)}

			{step === 'verify' && (
				<FaceVerify onCaptured={onCaptured} onContinue={verified} busy={faceBusy} />
			)}

			{step === 'confirm' && (
				<>
					{/* Every figure here is the SERVER's, not a count this page kept:
					    the recipient it resolved, the documents it matched, and the
					    retention flag it recorded. That is the whole reason initiate
					    and commit are two calls (§2.9). The fee is the same rule: the
					    server's own document count × the server's own transferPrice
					    (never a literal), and never a card — it is added to the
					    invoice below, exactly like the eVault add-on is. */}
					<KV
						rows={[
							['New owner', newOwnerCell],
							/* `KV` drops a row whose value is null, so a transfer that
							   carries no entity — one initiated before it was required —
							   has no such row rather than an empty one. */
							['Owning entity', entityCell],
							['Documents', String(summary?.documentCount ?? 0)],
							['Retention copy', initiated?.keepRetentionCopy ? 'Yes' : 'No'],
							[
								'Transfer fee',
								fee.unit
									? `${fee.quantity} × $${money(fee.unit)} = $${money(fee.total)}`
									: '—',
							],
						]}
					/>

					{/* transferPrice failed to load (0) — the fee row above already shows
					    "—", and Confirm is disabled above too. Nothing to retry inline: the
					    price comes from the add-on status the deck fetched on mount, so
					    closing and reopening the modal re-fetches it. */}
					{!fee.unit && (
						<div className="thelp">
							The transfer fee could not be loaded. Close and try again.
						</div>
					)}

					{/* The original is LEAVING the workspace. Said here, on the last
					    screen before an irreversible move, because it changes what the
					    transfer is: not a hand-off inside eVault but a delivery out of
					    it, after which nothing on this page can reach the document. */}
					{initiated?.newOwner.external && (
						<div className="note">
							{newOwner?.name || newOwner?.email} has no account in this
							workspace. The original will be delivered to {newOwner?.email} by
							email with its certificates, and it will no longer appear in
							eVault.
						</div>
					)}

					<div className="slist">
						{(summary?.documents ?? []).map(doc => (
							<div className="srow" key={doc._id}>
								<span className="fund">{formatDisplayId(doc.displayId)}</span>
								<span className="nm">{doc.name}</span>
							</div>
						))}
					</div>

					<div className="note">This confirmation expires in 15 minutes.</div>
					<div className="note">
						Added to this month&apos;s invoice. No card is charged now.
					</div>

					{!!error && <div className="merr">{error}</div>}
				</>
			)}

			{step === 'done' && committed && (
				<>
					<div className="tdone">
						{committed.transferredCount} original(s) now owned by{' '}
						{committed.newOwner?.name || committed.newOwner?.email || 'the new owner'}
						{/* The legal owner, named on the last screen too: it is what the
						    certificate and the audit trail now say, and the reader should
						    leave having seen it. */}
						{committed.newOwner?.entity
							? ` — ${committed.newOwner.entity.name}`
							: ''}
						{committed.newOwner?.external ? ' — delivered by email' : ''}.
					</div>
					<ul className="dones">
						<li>The change of custody has been recorded.</li>
						{committed.retentionCopyKept && <li>You kept a watermarked copy.</li>}
						{/* Never claims delivery the server has not confirmed. The notice
						    is sent after the commit returns, so the server can only say
						    it is on its way; whether it went is the `owner_notified` row
						    in the document's Activity log. */}
						{committed.notificationSent ? (
							<li>The new owner has been notified.</li>
						) : committed.notificationQueued ? (
							<li>
								{committed.newOwner?.external
									? /* For an external owner the email IS the delivery — they have
									     no eVault to open — so it is described as one rather than as
									     a notice about something they can go and find. */
										'The original is being emailed to the new owner, with its certificates.'
									: 'The new owner is being emailed a notice with the document attached.'}
							</li>
						) : null}
						{committed.newOwner?.external && (
							<li>It no longer appears in eVault.</li>
						)}
						<li>This transfer has been recorded in the audit trail.</li>
						{!!fee.unit && (
							<li>${money(fee.total)} was added to your invoice.</li>
						)}
					</ul>
				</>
			)}
		</Modal>
	);
};

export default TransferModal;
